import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LoginIcon from '@mui/icons-material/Login';
import { bffAuthService } from '../../services/BffAuthService';
import { AuthComponentStyles } from '../../common/ThemeColors';

const LoginPage = () => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const loggedOut = new URLSearchParams(window.location.search).get('logged_out') === 'true'
    || bffAuthService.isExplicitlyLoggedOut();

  useEffect(() => {
    if (!loggedOut) {
      bffAuthService.login();
    }
  }, [loggedOut]);

  const handleRetry = () => {
    setError(null);
    bffAuthService.login();
  };

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          padding: 2
        }}
      >
        <Box
          sx={{
            padding: 4,
            maxWidth: 500,
            width: '100%',
            textAlign: 'center'
          }}
        >
          <LoginIcon sx={{ fontSize: 64, mb: 2, color: 'error.main' }} />
          
          <Typography variant="h5" component="h2" gutterBottom color="error">
            {t('login.error.title', 'Login Failed')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          
          <Button
            variant="contained"
            size="large"
            onClick={handleRetry}
            startIcon={<LoginIcon />}
          >
            {t('login.retry', 'Retry Login')}
          </Button>
        </Box>
      </Box>
    );
  }

  if (loggedOut) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          padding: 2
        }}
      >
        <Box
          sx={{
            padding: 4,
            maxWidth: 500,
            width: '100%',
            textAlign: 'center'
          }}
        >
          <LoginIcon sx={{ fontSize: 64, mb: 2, color: 'primary.main' }} />

          <Typography variant="h5" component="h2" gutterBottom sx={AuthComponentStyles.authenticating.title}>
            {t('login.loggedOutTitle', 'You have signed out')}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('login.loggedOutMessage', 'Sign in again when you want to continue using Wise Memo.')}
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={handleRetry}
            startIcon={<LoginIcon />}
          >
            {t('buttonLogin', 'Login')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        padding: 2
      }}
    >
      <Box
        sx={{
          padding: 4,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center'
        }}
      >
        <CircularProgress size={64} />
        
        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3 }}>
          {t('login.redirecting', 'Redirecting to login...')}
        </Typography>
        
        <Typography variant="body1" color="text.secondary">
          {t('login.pleaseWait', 'Please wait while we redirect you to the authentication server.')}
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
